import { Box, Checkbox, TableCellProps, Td, useColorMode } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { COLLAPSED_CELL_MAX_HEIGHT_PX, longTextColumnMinWidth } from "./productTableLayout";

interface ProductFieldCellProps extends TableCellProps {
  value: string | number | boolean | null | undefined;
  isBoolean: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  fadeToColor?: string;
}

export const ProductFieldCell = ({
  value,
  isBoolean,
  isExpanded,
  onToggle,
  fadeToColor,
  ...cellProps
}: ProductFieldCellProps) => {
  const { colorMode } = useColorMode();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;
    const measure = () => setIsOverflowing(element.scrollHeight > COLLAPSED_CELL_MAX_HEIGHT_PX);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [value]);

  const isCollapsed = !isExpanded && isOverflowing;
  const fade = fadeToColor ?? (colorMode === "dark" ? "smBackgroundTertiary" : "smBackground");
  const minWidth = isBoolean ? undefined : longTextColumnMinWidth(value);

  return (
    <Td verticalAlign="top" {...cellProps} minWidth={minWidth ?? cellProps.minWidth}>
      {isBoolean ? (
        <Checkbox isDisabled={true} isChecked={!!value} />
      ) : (
        <Box
          ref={contentRef}
          position="relative"
          maxHeight={isCollapsed ? `${COLLAPSED_CELL_MAX_HEIGHT_PX}px` : "none"}
          overflow="hidden"
          cursor={isOverflowing ? "pointer" : "default"}
          onClick={isOverflowing ? onToggle : undefined}
        >
          <Box as="pre" fontFamily="body" whiteSpace="pre-wrap" overflowWrap="anywhere">
            {value}
          </Box>
          {isCollapsed && (
            <Box
              position="absolute"
              left="0"
              right="0"
              bottom="0"
              height="5"
              bgGradient={`linear(to bottom, transparent, ${fade})`}
              pointerEvents="none"
            />
          )}
        </Box>
      )}
    </Td>
  );
};
